import { useEffect } from "react";

export interface KeyboardShortcut {
	alt?: boolean;
	ctrl?: boolean;
	ctrlOrMeta?: boolean;
	key: string;
	meta?: boolean;
	shift?: boolean;
}

export interface UseKeyboardShortcutParams extends KeyboardShortcut {
	allowInEditable?: boolean;
	enabled?: boolean;
	onShortcut: (event: KeyboardEvent) => void;
	preventDefault?: boolean;
}

export type KeyboardShortcutPlatform = "mac" | "other";

export function useKeyboardShortcut({
	allowInEditable = false,
	enabled = true,
	onShortcut,
	preventDefault = true,
	...shortcut
}: UseKeyboardShortcutParams) {
	useEffect(() => {
		if (!enabled) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (!doesKeyboardShortcutMatch(event, shortcut, { allowInEditable })) return;

			if (preventDefault) {
				event.preventDefault();
			}
			onShortcut(event);
		};

		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [
		allowInEditable,
		enabled,
		onShortcut,
		preventDefault,
		shortcut.alt,
		shortcut.ctrl,
		shortcut.ctrlOrMeta,
		shortcut.key,
		shortcut.meta,
		shortcut.shift,
	]);
}

export function getKeyboardShortcutKeys(shortcut: KeyboardShortcut, platform = getKeyboardShortcutPlatform()) {
	const keys: string[] = [];

	if (shortcut.ctrlOrMeta === true) {
		keys.push(platform === "mac" ? "⌘" : "Ctrl");
	} else {
		if (shortcut.ctrl === true) keys.push("Ctrl");
		if (shortcut.meta === true) keys.push(platform === "mac" ? "⌘" : "Meta");
	}
	if (shortcut.alt === true) keys.push(platform === "mac" ? "⌥" : "Alt");
	if (shortcut.shift === true) keys.push(platform === "mac" ? "⇧" : "Shift");

	keys.push(shortcut.key.toUpperCase());

	return keys;
}

export function getKeyboardShortcutPlatform(): KeyboardShortcutPlatform {
	if (typeof navigator === "undefined") return "other";
	return navigator.platform.toLowerCase().includes("mac") ? "mac" : "other";
}

export function doesKeyboardShortcutMatch(
	event: KeyboardEvent,
	shortcut: KeyboardShortcut,
	options: { allowInEditable?: boolean } = {},
) {
	const eventKey = event.key?.toLowerCase();
	const shortcutKey = shortcut.key?.toLowerCase();

	return (
		eventKey != null &&
		shortcutKey != null &&
		eventKey === shortcutKey &&
		event.shiftKey === (shortcut.shift ?? false) &&
		event.altKey === (shortcut.alt ?? false) &&
		matchesCtrlAndMeta(event, shortcut) &&
		!event.defaultPrevented &&
		!event.isComposing &&
		!event.repeat &&
		(options.allowInEditable === true || !isEditableShortcutTarget(event.target))
	);
}

export function isEditableShortcutTarget(target: EventTarget | null) {
	if (!(target instanceof Element)) return false;

	const element = target.closest("input, textarea, select, [contenteditable='true'], [role='textbox']");
	if (element == null) return false;

	return !element.hasAttribute("readonly") && !element.hasAttribute("disabled");
}

function matchesCtrlAndMeta(event: KeyboardEvent, shortcut: KeyboardShortcut) {
	if (shortcut.ctrlOrMeta === true) {
		return event.ctrlKey || event.metaKey;
	}

	return event.ctrlKey === (shortcut.ctrl ?? false) && event.metaKey === (shortcut.meta ?? false);
}
